import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import general from '../../utils/general'
import useUserRole from '../../utils/hooks/use-user-role'
import { UserTypeEnum } from '../../utils/enums'
import useI18n from '../../i18n/useI18n'
import RangeFilterCard from './components/RangeFilterCard'
import KPISection from './components/KPISection'
import ApplicationsTrendCard from './components/ApplicationsTrendCard'
import ApplicationsStatusCard from './components/ApplicationsStatusCard'
import TopListCard from './components/TopListCard'
import LatestStatusChangesCard from './components/LatestStatusChangesCard'
import LatestApplicationsCard from './components/LatestApplicationsCard'
import AlertsCard from './components/AlertsCard'
import {
  emptyDashboardData,
  formatNumber,
  rangeFilterDefaults,
  getRangeLabelKey
} from './utils'
import './index.scss'

const normalizeDateInput = (value) => {
  if (!value) return ''
  // ISO string -> YYYY-MM-DD for date inputs
  return value.slice(0, 10)
}

const Dashboard = () => {
  const dispatch = useDispatch()
  const userRole = useUserRole()
  const { t } = useI18n()
  const [filters, setFilters] = useState(rangeFilterDefaults)
  const [dashboardData, setDashboardData] = useState(emptyDashboardData)
  const [loading, setLoading] = useState(false)
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false)

  const buildQuery = useCallback((filterValues) => {
    const normalized = filterValues || rangeFilterDefaults
    const query = {
      range: normalized.range || '30d'
    }

    if (query.range === 'custom') {
      if (normalized.from) {
        query.from = normalized.from
      }
      if (normalized.to) {
        query.to = normalized.to
      }
    }

    return query
  }, [])

  const sanitizeFilters = useCallback((draft, options = {}) => {
    const { preserveNonCustomDates = false } = options
    const normalized = { ...rangeFilterDefaults, ...draft }
    if (!preserveNonCustomDates && normalized.range !== 'custom') {
      normalized.from = ''
      normalized.to = ''
    }
    return normalized
  }, [])

  const buildFiltersFromRangeMeta = useCallback((rangeMeta) => {
    if (!rangeMeta) return null
    const type = rangeMeta.type || rangeFilterDefaults.range
    return sanitizeFilters(
      {
        range: type,
        from: normalizeDateInput(rangeMeta.from),
        to: normalizeDateInput(rangeMeta.to)
      },
      { preserveNonCustomDates: true }
    )
  }, [sanitizeFilters])

  const fetchDashboard = useCallback((filterValues) => {
    const effectiveFilters = filterValues || filters
    setLoading(true)

    dispatch(
      ApiCallActions.Get({
        url: 'dashboard',
        query: buildQuery(effectiveFilters),
        showAlertOnError: true,
        onSuccess: ({ data }) => {
          const payload = data?.data || data || {}
          const nextDashboardData = {
            meta: payload.meta || {},
            kpis: payload.kpis || {},
            charts: payload.charts || {},
            lists: payload.lists || {},
            alerts: payload.alerts || []
          }
          setDashboardData(nextDashboardData)
          const metaFilters = buildFiltersFromRangeMeta(nextDashboardData.meta?.range)
          if (metaFilters) {
            setFilters(metaFilters)
          }
          setLoading(false)
        },
        onError: () => {
          setDashboardData(emptyDashboardData)
          setLoading(false)
        }
      })
    )
  }, [buildFiltersFromRangeMeta, buildQuery, dispatch, filters])

  useEffect(() => {
    fetchDashboard(rangeFilterDefaults)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFilterChange = (nextFilters) => {
    setFilters(sanitizeFilters(nextFilters))
  }

  const handleFilterSubmit = (submittedFilters) => {
    const normalized = sanitizeFilters(submittedFilters)
    if (
      normalized.range === 'custom' &&
      (!normalized.from || !normalized.to)
    ) {
      general.notificationError(t('DASHBOARD_FILTER_CUSTOM_RANGE_ERROR'))
      return
    }
    setFilters(normalized)
    fetchDashboard(normalized)
    setIsFilterPanelOpen(false)
  }

  const handleClearFilters = () => {
    setFilters(rangeFilterDefaults)
    fetchDashboard(rangeFilterDefaults)
  }

  const handleRefresh = () => {
    fetchDashboard(filters)
  }

  const kpiItems = useMemo(() => {
    const kpis = dashboardData.kpis || {}
    if (userRole === UserTypeEnum.AGENT) {
      return [
        {
          key: 'myStudents',
          label: t('DASHBOARD_KPI_AGENT_MY_STUDENTS_LABEL'),
          value: kpis.myStudents || 0,
          helper: t('DASHBOARD_KPI_AGENT_MY_STUDENTS_HELPER'),
          icon: 'zmdi zmdi-accounts',
          accent: 'indigo'
        },
        {
          key: 'myApplicationsInRange',
          label: t('DASHBOARD_KPI_AGENT_MY_APPLICATIONS_LABEL'),
          value: kpis.myApplicationsInRange || 0,
          helper: t('DASHBOARD_KPI_RANGE_HELPER'),
          icon: 'zmdi zmdi-file-text',
          accent: 'green'
        },
        {
          key: 'activeInRange',
          label: t('DASHBOARD_KPI_AGENT_ACTIVE_LABEL'),
          value: kpis.activeInRange || 0,
          helper: t('DASHBOARD_KPI_AGENT_ACTIVE_HELPER'),
          icon: 'zmdi zmdi-time-restore',
          accent: 'orange'
        },
        {
          key: 'acceptedInRange',
          label: t('DASHBOARD_KPI_AGENT_ACCEPTED_LABEL'),
          value: kpis.acceptedInRange || 0,
          helper: t('DASHBOARD_KPI_RANGE_HELPER'),
          icon: 'zmdi zmdi-check-circle',
          accent: 'teal'
        }
      ]
    }

    if (userRole === UserTypeEnum.STUDENT) {
      return [
        {
          key: 'myApplicationsTotal',
          label: t('DASHBOARD_KPI_STUDENT_TOTAL_LABEL'),
          value: kpis.myApplicationsTotal || 0,
          helper: t('DASHBOARD_KPI_RANGE_HELPER'),
          icon: 'zmdi zmdi-assignment',
          accent: 'blue'
        },
        {
          key: 'myActiveApplications',
          label: t('DASHBOARD_KPI_STUDENT_ACTIVE_LABEL'),
          value: kpis.myActiveApplications || 0,
          helper: t('DASHBOARD_KPI_STUDENT_ACTIVE_HELPER'),
          icon: 'zmdi zmdi-hourglass-alt',
          accent: 'orange'
        },
        {
          key: 'myAcceptedApplications',
          label: t('DASHBOARD_KPI_STUDENT_ACCEPTED_LABEL'),
          value: kpis.myAcceptedApplications || 0,
          helper: t('DASHBOARD_KPI_STUDENT_ACCEPTED_HELPER'),
          icon: 'zmdi zmdi-check',
          accent: 'green'
        },
        {
          key: 'missingDocumentsCount',
          label: t('DASHBOARD_KPI_STUDENT_MISSING_DOC_LABEL'),
          value: kpis.missingDocumentsCount || 0,
          helper: t('DASHBOARD_KPI_STUDENT_MISSING_DOC_HELPER'),
          icon: 'zmdi zmdi-folder',
          accent: 'red'
        }
      ]
    }

    return [
      {
        key: 'universities',
        label: t('DASHBOARD_KPI_UNIVERSITIES_LABEL'),
        value: kpis.universities?.total || 0,
        helper: t('DASHBOARD_KPI_UNIVERSITIES_HELPER', { count: formatNumber(kpis.universities?.active || 0) }),
        icon: 'zmdi zmdi-city-alt',
        accent: 'purple'
      },
      {
        key: 'programs',
        label: t('DASHBOARD_KPI_PROGRAMS_LABEL'),
        value: kpis.programs?.total || 0,
        helper: t('DASHBOARD_KPI_PROGRAMS_HELPER', { count: formatNumber(kpis.programs?.active || 0) }),
        icon: 'zmdi zmdi-graduation-cap',
        accent: 'teal'
      },
      {
        key: 'agents',
        label: t('DASHBOARD_KPI_AGENTS_LABEL'),
        value: kpis.agents?.total || 0,
        helper: t('DASHBOARD_KPI_AGENTS_HELPER', { count: formatNumber(kpis.agents?.contractActive || 0) }),
        icon: 'zmdi zmdi-badge-check',
        accent: 'indigo'
      },
      {
        key: 'students',
        label: t('DASHBOARD_KPI_STUDENTS_LABEL'),
        value: kpis.students?.total || 0,
        helper: t('DASHBOARD_KPI_STUDENTS_HELPER'),
        icon: 'zmdi zmdi-accounts',
        accent: 'orange'
      },
      {
        key: 'applications',
        label: t('DASHBOARD_KPI_APPLICATIONS_LABEL'),
        value: kpis.applications?.totalInRange || 0,
        helper: t('DASHBOARD_KPI_RANGE_HELPER'),
        icon: 'zmdi zmdi-file',
        accent: 'green'
      }
    ]
  }, [dashboardData.kpis, t, userRole])

  const charts = dashboardData.charts || {}
  const lists = dashboardData.lists || {}
  const alerts = dashboardData.alerts || []
  const rangeMeta = dashboardData.meta?.range || {}
  const computedRangeType = rangeMeta.type || filters.range
  const rangeLabel = t(getRangeLabelKey(computedRangeType))
  const isStudent = userRole === UserTypeEnum.STUDENT
  const statusChangesTitle = isStudent
    ? t('DASHBOARD_CARD_STATUS_CHANGES_TITLE_STUDENT')
    : t('DASHBOARD_CARD_STATUS_CHANGES_TITLE')

  const openFilterPanel = () => setIsFilterPanelOpen(true)
  const closeFilterPanel = () => setIsFilterPanelOpen(false)

  return (
    <section className="content dashboard-page">
      <div className={`dashboard-filter-trigger ${isFilterPanelOpen ? 'is-hidden' : ''}`}>
        <button type="button" className="dashboard-filter-toggle" onClick={openFilterPanel}>
          <i className="zmdi zmdi-filter-list"></i>
          <span>{t('COMMON_FILTERS')}</span>
        </button>
      </div>
      <div className="block-header">
        <div className="row">
          <div className="col-12 text-left">
            <h2>
              {t('DASHBOARD_TITLE')}

              <small>{t('DASHBOARD_SUBTITLE')}{" "}(
                {rangeLabel}
                {" "} - {" "}
                {rangeMeta.from && <span>{general.formatDate(rangeMeta.from)}</span>}
                {" "} - {" "}
                {rangeMeta.to && <span>{general.formatDate(rangeMeta.to)}</span>}
                )</small>
            </h2>
          </div>
        </div>
        {/* <div className="range-meta">
          <span className="badge badge-info">
            {t('DASHBOARD_RANGE_META_BADGE', { range: rangeLabel })}
          </span>
          {rangeMeta.from && <span>{t('DASHBOARD_RANGE_META_FROM', { date: general.formatDate(rangeMeta.from) })}</span>}
          {rangeMeta.to && <span>{t('DASHBOARD_RANGE_META_TO', { date: general.formatDate(rangeMeta.to) })}</span>}
        </div> */}
      </div>

      <div className="container-fluid">
        <div className="row clearfix dashboard-layout">
          <div className="col-12" style={{ marginBottom: '20px' }}>
            <KPISection items={kpiItems} loading={loading} />
          </div>

          <div className="col-xl-8 col-lg-12">
            <div className="dashboard-column">
              <LatestStatusChangesCard
                items={lists.latestStatusChanges || []}
                loading={loading}
                title={statusChangesTitle}
              />

              <ApplicationsTrendCard
                data={charts.applicationsTrend || []}
                loading={loading}
              />

              {isStudent && (
                <LatestApplicationsCard
                  items={lists.latestApplications || []}
                  loading={loading}
                />
              )}

              {/* <AlertsCard alerts={alerts} loading={loading} /> */}
            </div>
          </div>

          <div className="col-xl-4 col-lg-12">
            <div className="dashboard-column dashboard-column-right">
              <ApplicationsStatusCard
                data={charts.applicationsByStatus || {}}
                loading={loading}
              />

              {!isStudent && (
                <>
                  <TopListCard
                    title={t('DASHBOARD_CARD_TOP_UNIVERSITIES_TITLE')}
                    subtitle={t('DASHBOARD_CARD_TOP_UNIVERSITIES_SUBTITLE')}
                    items={lists.topUniversitiesByApplications || []}
                    loading={loading}
                  />
                  <TopListCard
                    title={t('DASHBOARD_CARD_TOP_PROGRAMS_TITLE')}
                    subtitle={t('DASHBOARD_CARD_TOP_PROGRAMS_SUBTITLE')}
                    items={lists.topProgramsByApplications || []}
                    loading={loading}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {isFilterPanelOpen && (
        <>
          <div className="dashboard-filter-overlay" onClick={closeFilterPanel}></div>
          <aside className="dashboard-filter-panel open">
            <div className="dashboard-filter-panel__header">
              <div>
                <p className="title">{t('COMMON_FILTERS')}</p>
              </div>
              <button type="button" className="close-btn btn btn-link" onClick={closeFilterPanel}>
                <i className="zmdi zmdi-close"></i>
              </button>
            </div>
            <div className="dashboard-filter-panel__body">
              <RangeFilterCard
                filters={filters}
                onFilterChange={handleFilterChange}
                onFilterSubmit={handleFilterSubmit}
                onClearFilters={handleClearFilters}
                onRefresh={handleRefresh}
                loading={loading}
              />
            </div>
          </aside>
        </>
      )}
    </section>
  )
}

export default Dashboard